import { Link } from 'react-router-dom';
import { toolRegistry, SITE_NAME, SITE_SOCIAL_LINKS } from '../../data/toolRegistry';
import { blogRegistry } from '../../data/blogRegistry';

/**
 * Mega Footer — curated link groups with a branded CTA band.
 */
export function MegaFooter() {
  const getResourceLinkText = (title: string): string => {
    // Keep footer anchors concise for link-structure SEO checks.
    const beforeDashOrPipe = title.split(/[|—]/)[0]?.trim() || title;
    return beforeDashOrPipe.length > 64 ? `${beforeDashOrPipe.slice(0, 61).trimEnd()}...` : beforeDashOrPipe;
  };

  const featuredTools = toolRegistry.slice(0, 6);
  const featuredResources = [...blogRegistry]
    .sort((a, b) => b.publishDate.localeCompare(a.publishDate))
    .slice(0, 6);

  const microToolLinks = [
    { label: 'WebP to PNG/JPG', to: '/micro-tools/webp-to-png-jpg' },
    { label: 'PNG to JPG', to: '/micro-tools/png-to-jpg' },
    { label: 'JPG to PNG', to: '/micro-tools/jpg-to-png' },
    { label: 'Browse Micro Tools', to: '/micro-tools' },
  ];

  return (
    <footer className="bg-stone-900 text-stone-300 mt-auto">
      <div className="max-w-[1200px] mx-auto px-6 pt-14 pb-10">
        <div className="rounded-3xl border border-stone-800/80 bg-gradient-to-br from-stone-900 via-stone-900 to-stone-800 p-6 md:p-8 mb-10">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-stone-400 mb-2">Solo OS</p>
              <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight text-white mb-2">
                Build your solo stack, locally.
              </h2>
              <p className="text-[14px] text-stone-400 max-w-[560px] leading-relaxed">
                Free, browser-first tools for proposals, invoices, and micro conversions. Own your workflow, keep your data private.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/tools/upi-invoice-generator-india"
                className="inline-flex items-center justify-center rounded-xl bg-white text-stone-900 text-[13px] font-semibold px-4 py-2.5 hover:bg-stone-100 transition-colors"
              >
                Create an invoice
              </Link>
              <Link
                to="/micro-tools"
                className="inline-flex items-center justify-center rounded-xl border border-stone-700 text-stone-200 text-[13px] font-semibold px-4 py-2.5 hover:border-stone-500 hover:text-white transition-colors"
              >
                Micro Tools
              </Link>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          <div>
            <p className="text-[11px] font-bold text-stone-400 uppercase tracking-[0.14em] mb-4">
              Popular Tools
            </p>
            <ul className="space-y-2.5">
              {featuredTools.map(tool => (
                <li key={tool.slug}>
                  <Link
                    to={tool.canonicalPath}
                    className="text-[13px] text-stone-400 hover:text-white transition-colors leading-snug block"
                  >
                    {tool.h1}
                  </Link>
                </li>
              ))}
            </ul>
            <Link
              to="/"
              className="inline-flex items-center mt-4 text-[12px] font-semibold text-stone-300 hover:text-white transition-colors"
            >
              Browse all tools
            </Link>
          </div>

          <div>
            <p className="text-[11px] font-bold text-stone-400 uppercase tracking-[0.14em] mb-4">
              Micro Tools
            </p>
            <ul className="space-y-2.5">
              {microToolLinks.map(link => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-[13px] text-stone-400 hover:text-white transition-colors leading-snug block"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-[11px] font-bold text-stone-400 uppercase tracking-[0.14em] mb-4">
              Resources
            </p>
            <ul className="space-y-2.5">
              {featuredResources.map(article => (
                <li key={article.slug}>
                  <Link
                    to={article.canonicalPath}
                    className="text-[13px] text-stone-400 hover:text-white transition-colors leading-snug block"
                  >
                    {getResourceLinkText(article.title)}
                  </Link>
                </li>
              ))}
            </ul>
            <Link
              to="/resources"
              className="inline-flex items-center mt-4 text-[12px] font-semibold text-stone-300 hover:text-white transition-colors"
            >
              Browse all resources
            </Link>
          </div>

          <div>
            <p className="text-[11px] font-bold text-stone-400 uppercase tracking-[0.14em] mb-4">
              Company
            </p>
            <ul className="space-y-2.5">
              <li>
                <Link to="/" className="text-[13px] text-stone-400 hover:text-white transition-colors leading-snug block">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/micro-tools" className="text-[13px] text-stone-400 hover:text-white transition-colors leading-snug block">
                  Micro Tools
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-[13px] text-stone-400 hover:text-white transition-colors leading-snug block">
                  FAQ
                </Link>
              </li>
              <li>
                <Link to="/sign" className="text-[13px] text-stone-400 hover:text-white transition-colors leading-snug block">
                  E-Sign Tool
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-[13px] text-stone-400 hover:text-white transition-colors leading-snug block">
                  Contact
                </Link>
              </li>
              <li>
                <Link to="/legal/privacy-policy" className="text-[13px] text-stone-400 hover:text-white transition-colors leading-snug block">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/legal/terms-of-service" className="text-[13px] text-stone-400 hover:text-white transition-colors leading-snug block">
                  Terms of Service
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-stone-800">
        <div className="max-w-[1200px] mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="w-6 h-6 rounded overflow-hidden bg-stone-800 flex items-center justify-center">
              <img src="/logo.png" alt="Solo OS" width={24} height={24} className="w-full h-full object-cover opacity-60" />
            </div>
            <span className="text-[13px] font-semibold text-stone-500">{SITE_NAME}</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-4 text-[12px] text-stone-600">
            <span>© {new Date().getFullYear()} {SITE_NAME}. Browser-first workflows.</span>
            <a href={SITE_SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
              Instagram
            </a>
            <a href={SITE_SOCIAL_LINKS.x} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
              X
            </a>
            <a href={SITE_SOCIAL_LINKS.linkedin} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
              LinkedIn
            </a>
            <a href={SITE_SOCIAL_LINKS.youtube} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
              YouTube
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
